import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../componentes/Header";
import Footer from "../componentes/Footer";
import Contato from "../componentes/Contato";
import DashboardPreview from "../componentes/DashboardPreview";
import Figura from "../componentes/Figura";
import Reveal from "../componentes/Reveal";
import {
  ArrowLeft,
  Sparkles,
  Database,
  Layout,
  MessageCircle,
  Workflow,
} from "../componentes/Icons";

const NUMEROS = [
  { n: "10", l: "Módulos de BI" },
  { n: "21", l: "Rotas na app" },
  { n: "~46", l: "Componentes" },
  { n: "~12k", l: "Linhas de TS" },
  { n: "8", l: "Workflows n8n" },
];

const TAGS = [
  "Next.js 16",
  "React 19",
  "TypeScript",
  "Tailwind CSS 4",
  "Supabase",
  "PostgreSQL",
  "RLS / RPC",
  "n8n / ETL",
  "pgvector / RAG",
  "WhatsApp API",
  "Whisper",
  "Vercel",
];

const MODULOS = [
  ["Comercial", "Metas (mês/trim/ano), funis por canal, propostas quentes e estoque por praça."],
  ["Financeiro", "Comissões, repasses, VGV realizado vs projetado, receita líquida e inadimplência."],
  ["Performance de Corretores", "Ranking por VGV, funil individual, conversão e meta vs realizado."],
  ["Inteligência de Perdas", "Win rate, VGV perdido, motivos de perda e oportunidades recuperáveis."],
  ["Alertas & Saúde do Pipeline", "Negócios parados (semáforo), propostas esfriando e VGV em risco."],
  ["Captação & Leads", "ROI por origem, conversão por canal e tempo de 1º contato."],
  ["Velocidade & Forecast", "Tempo de ciclo, gargalos por etapa e forecast ponderado por probabilidade."],
  ["Empreendimentos", "Curva de absorção, ritmo de vendas e alerta de estoque crítico."],
  ["Demanda vs Oferta", "Cruzamento entre o perfil procurado e o estoque para apontar lacunas."],
  ["Relatórios Automáticos", "Agendamento e envio por e-mail/WhatsApp, com histórico de entregas."],
];

const PRINTS = [
  { src: "/dashboard-1.png", alt: "Visão geral — Indicadores", cap: "Visão Geral · Indicadores consolidados" },
  { src: "/dashboard-2.png", alt: "Ata & Metas", cap: "Gestão de Ata & Metas (VGV / Caixa)" },
  { src: "/dashboard-3.png", alt: "Performance de Corretores", cap: "Performance de Corretores · Ranking e funil" },
  { src: "/dashboard-4.png", alt: "Configurações e temas", cap: "Configurações · Módulos e paleta white-label" },
];

function DashboardImobiliario() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div>
      <Header />

      <header className="proj-hero">
        <div className="container">
          <div className="proj-hero-inner">
            <Link to="/" className="proj-back">
              <ArrowLeft /> Voltar para o portfólio
            </Link>
            <div className="flagship-tagrow" style={{ marginBottom: 6 }}>
              <span className="badge badge-ok">
                <Sparkles width={14} height={14} /> Projeto em destaque
              </span>
              <span className="badge">Business Intelligence</span>
            </div>
            <h1>Dashboard Imobiliário</h1>
            <p>
              Plataforma de Business Intelligence que se integra ao CRM e ao
              WhatsApp, transformando os dados operacionais de uma imobiliária em
              métricas de gestão em tempo real.
            </p>
          </div>
        </div>
      </header>

      <div className="container">
        <Reveal className="proj-showcase">
          <DashboardPreview />
        </Reveal>
      </div>

      <section className="section">
        <div className="container">
          <div className="proj-content">
            <article className="proj-article">
              <h2>O que eu construí</h2>
              <p>
                Desenvolvi uma plataforma SaaS de Business Intelligence voltada
                para donos e gestores de imobiliárias. A ideia não era criar mais
                um CRM, e sim uma <strong>camada de inteligência paralela</strong>{" "}
                que lê os dados operacionais e os converte em decisão.
              </p>
              <p>
                Integrei o sistema ao <strong>CRM (Vista)</strong> via um pipeline
                de ETL automatizado, armazenei tudo em um data store próprio no{" "}
                <strong>Supabase/PostgreSQL</strong> e construí um painel
                analítico modular, configurável e white-label. Em paralelo,
                acoplei um <strong>chatbot de WhatsApp com IA</strong> que atende
                clientes e corretores e alimenta o próprio dashboard.

              </p>

              <Reveal as="div" className="numbers">
                {NUMEROS.map((x) => (
                  <div className="number-card" key={x.l}>
                    <div className="n">{x.n}</div>
                    <div className="l">{x.l}</div>
                  </div>
                ))}
              </Reveal>

              <h2>Arquitetura de dados</h2>
              <p>
                Montei um fluxo de dados de ponta a ponta, idempotente e à prova
                de reprocessamento:
              </p>
              <ul>
                <li>
                  <strong>CRM (REST API)</strong> como fonte — com paginação,
                  autenticação e normalização dos dados.
                </li>
                <li>
                  <strong>n8n</strong> rodando o ETL e jobs agendados, com{" "}
                  <strong>UPSERT idempotente</strong> e snapshots append-only.
                </li>
                <li>
                  <strong>Supabase (PostgreSQL)</strong> com RLS, RPC e uma função
                  que <strong>reconstrói o estado de cada negócio</strong> no fim
                  de qualquer período (modelagem temporal).
                </li>
                <li>
                  <strong>Next.js (App Router)</strong> consumindo tudo, com a
                  configuração do tenant aplicada em runtime.
                </li>
              </ul>

              <h2>Os 10 módulos de inteligência</h2>
              <p>
                Modelei o produto como um registro central de módulos (1 core + 9
                ativáveis). A sidebar, os guards de rota e a tela de configurações
                derivam todos dessa fonte única de verdade.
              </p>
              <ul>
                {MODULOS.map(([nome, desc]) => (
                  <li key={nome}>
                    <strong>{nome}</strong> — {desc}
                  </li>
                ))}
              </ul>

              <h2>Plataforma &amp; experiência</h2>
              <p>
                Cuidei para que o produto fosse demonstrável e configurável sem
                fricção:
              </p>
              <ul>
                <li>
                  <strong>White-label</strong> — 6 paletas selecionáveis,
                  propagadas via CSS variables, e identidade configurável por
                  tenant, aplicada ao vivo.
                </li>
                <li>
                  <strong>Módulos ativáveis</strong> — uma tela de configurações
                  central liga/desliga módulos (com os core protegidos).
                </li>
                <li>
                  <strong>Modo Apresentação e Modo TV</strong> — fullscreen com
                  slides para o comitê comercial e exibição de KPIs em telão.
                </li>
                <li>
                  <strong>Exportação de relatórios</strong> — PDF por imóvel e
                  Excel geral, gerados no próprio browser.
                </li>
              </ul>

              <h2>Módulo SIA — Chatbot no WhatsApp</h2>
              <p>
                Construí um subsistema de atendimento automatizado via WhatsApp
                (cliente / corretor / captação) totalmente integrado ao dashboard:
              </p>
              <ul>
                <li>
                  Suporte a <strong>mensagens de voz</strong> com transcrição via
                  Whisper.
                </li>
                <li>
                  <strong>RAG</strong> sobre o catálogo de imóveis usando
                  embeddings vetoriais (pgvector).
                </li>
                <li>
                  Análise de sessões com IA para consolidar e classificar leads,
                  além de rotinas proativas (briefing matinal e alertas de
                  esfriamento).
                </li>
              </ul>

              <h2>Engenharia aplicada</h2>
              <p>
                Adotei uma arquitetura <strong>mock-first</strong>: o produto roda
                100% navegável sem banco, com cada mock no formato exato da futura
                função de dados real — o que permitiu trocar a fonte de dados sem
                refatorar a UI. Mantive <strong>tipagem rigorosa</strong> (
                <code>tsc --noEmit</code>) e <code>next build</code> limpos a cada
                entrega, com commits atômicos por feature e um design system 100%
                em tokens (zero cores hardcoded).
              </p>

              <h2>Telas do produto</h2>
              <p>
                Alguns recortes da aplicação em funcionamento:
              </p>
              <div className="gallery">
                {PRINTS.map((p) => (
                  <Figura key={p.src} src={p.src} alt={p.alt} caption={p.cap} />
                ))}
              </div>
            </article>

            {/* Aside */}
            <aside className="proj-aside">
              <div className="aside-card">
                <h4>Tecnologias</h4>
                <div className="aside-tags">
                  {TAGS.map((t) => (
                    <span className="tag" key={t}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>

              <div className="aside-card">
                <h4>Destaques</h4>
                <div className="aside-meta">
                  <div className="row">
                    <span className="ic">
                      <Database />
                    </span>
                    <span>
                      <small>Engenharia de dados</small>
                      <b>ETL · RLS · RPC · modelagem temporal</b>
                    </span>
                  </div>
                  <div className="row">
                    <span className="ic">
                      <Layout />
                    </span>
                    <span>
                      <small>Frontend de produto</small>
                      <b>White-label · modular · responsivo</b>
                    </span>
                  </div>
                  <div className="row">
                    <span className="ic">
                      <MessageCircle />
                    </span>
                    <span>
                      <small>IA aplicada</small>
                      <b>Chatbot · voz · RAG · análise</b>
                    </span>
                  </div>
                  <div className="row">
                    <span className="ic">
                      <Workflow />
                    </span>
                    <span>
                      <small>Automação</small>
                      <b>n8n · jobs proativos · idempotência</b>
                    </span>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <Contato
        titulo="Quer uma solução de dados como esta?"
        texto="Posso desenhar e construir uma plataforma de BI, automação ou IA sob medida para o seu negócio. Vamos conversar?"
      />

      <Footer />
    </div>
  );
}

export default DashboardImobiliario;
